/**
 * The cross-section's arithmetic (spec §4d).
 *
 * Everything here is pure: no React, no DOM, no scales. The component owns
 * the scales, the events and the copy; this module owns the geometry that
 * decides how tall a metre is, where the cut band stops being a cut band and
 * which columns are a hole in the data rather than a zero difference — so
 * each of those can be asserted directly instead of inspected by eye.
 *
 * Coordinates in and out are **metres**: `o` is a signed offset from the
 * centreline (left negative, looking up-station, matching `projectPoint`)
 * and `z` is a real elevation. Nothing here knows about pixels except
 * `pixelsPerMetreZ` / `zDomain`, which exist precisely to make the drawn
 * vertical exaggeration the one on the label.
 */

/**
 * Selectable vertical exaggerations: a 1-2-5 ladder from 0.2× to 100×.
 *
 * It runs BOTH ways out of 1× because the two ends fail differently and both
 * failures are real. The shallow end is the one a reader notices: a 6 m stack
 * over a 10 m swath needs 0.5× to fit a 300 px pane, so a list that bottomed
 * out at 1× drew 120 % of the structure — the top and the bottom of the road
 * simply were not on the drawing, with nothing saying so. The deep end is the
 * one the FEATURE is about: a 40 mm wearing course over a 20 m swath is 12 px
 * at 10×, on a pane whose whole job is reading layer thickness off a drawing;
 * it wants 150×.
 *
 * The 1-2-5 spacing is not decoration. `autoExaggeration` snaps to the nearest
 * stop multiplicatively, so a reader lands at worst √r off the exaggeration
 * they wanted; with no gap wider than 2.5× that is at worst 1.58×, which is
 * what keeps every auto-chosen section inside the 38–95 % of pane height the
 * ladder promises. Widening a gap to shorten the list breaks that promise.
 */
export const VE_PRESETS = [0.2, 0.5, 1, 2, 5, 10, 20, 50, 100] as const;

/**
 * What `Auto` may choose from — the whole ladder.
 *
 * This used to be the coarse end only (`[1, 2, 5, 10]`), on the reasoning that
 * a section nobody asked to be stretched is how a tolerance band gets mistaken
 * for a fault. The reasoning was sound and the conclusion was wrong, in both
 * directions: withholding the deep stops from `Auto` does not make a 40 mm
 * layer read honestly, it makes it 4 % of the pane and unreadable, and
 * withholding the shallow ones made `Auto` CLIP. What answers the original
 * worry is that the factor is stated — on the `Auto` button, on the elevation
 * axis, in the aria label and on the printed sheet — not that it is small.
 */
export const VE_AUTO_PRESETS = VE_PRESETS;

/**
 * What a pane with no geometry to measure falls back to: the undistorted
 * drawing, not the shallowest stop. `0.2×` is an answer to a section too tall
 * to fit; it is not an answer to "the pane has not been laid out yet".
 */
const VE_FALLBACK = 1;

export type VePreset = (typeof VE_PRESETS)[number];

export interface SectionSpan {
  lo: number;
  hi: number;
  /** False when the whole row is NaN — a bin the analysis never covered. */
  valid: boolean;
}

export interface SectionPoint {
  /** Signed offset from the centreline, metres. Left is negative. */
  o: number;
  /** Elevation, metres. */
  z: number;
}

export interface BandPolygon {
  kind: 'cut' | 'fill';
  points: SectionPoint[];
}

/** An inclusive index range of columns. */
export interface OffsetRun {
  from: number;
  to: number;
}

const finite = (v: number) => Number.isFinite(v);

/**
 * The elevation range both surfaces occupy. Cross-sections are read against
 * a shared vertical scale, so the span has to cover whichever surface is
 * present — a row where only the survey was measured still has a section.
 */
export function sectionSpans(zTop: Float32Array, zBottom: Float32Array): SectionSpan {
  return sectionSpansAcross([zTop, zBottom]);
}

/**
 * The elevation range EVERY drawn plane occupies — the stack generalisation of
 * `sectionSpans` (multi-layer thickness §6.2).
 *
 * A stack draws N as-built levels and N design levels on one vertical scale.
 * Spanning only the first pair would push the formation, and every design
 * reference line, off the bottom of the pane on a four-level road.
 *
 * The length is the SHORTEST plane's, exactly as the pair version took
 * `Math.min` of the two: a plane list is one station line's worth of columns
 * and they are all the same length, but a short one must never index past its
 * own buffer.
 */
export function sectionSpansAcross(planes: readonly Float32Array[]): SectionSpan {
  let lo = Infinity;
  let hi = -Infinity;
  const n = planes.length === 0 ? 0 : Math.min(...planes.map((p) => p.length));
  for (const plane of planes) {
    for (let i = 0; i < n; i++) {
      const v = plane[i];
      if (!finite(v)) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  return finite(lo) ? { lo, hi, valid: true } : { lo: 0, hi: 1, valid: false };
}

/**
 * Pixels per metre of ELEVATION at a given exaggeration.
 *
 * This is the whole contract of the `{ve}×` label (ruling T13.2): the
 * horizontal scale is `plotW / offsetSpan` px per metre, and the vertical
 * scale is exactly `ve` times that. Any other y-scale — "fit the span to the
 * pane", say — makes the number on the label a decoration, and a section
 * drawn at an unstated exaggeration is how a 40 mm dip gets read as a metre.
 */
export function pixelsPerMetreZ(ve: number, offsetSpan: number, plotW: number): number {
  if (!(offsetSpan > 0) || !(plotW > 0) || !(ve > 0)) return 0;
  return ve * (plotW / offsetSpan);
}

/**
 * The elevation domain to draw, centred on the section, at exactly `ve` times
 * the horizontal scale. The pane shows whatever fits; `autoExaggeration`
 * exists so that "whatever fits" is, by default, the section itself.
 */
export function zDomain(
  span: SectionSpan,
  ve: number,
  offsetSpan: number,
  plotW: number,
  plotH: number,
): [number, number] {
  const mid = (span.lo + span.hi) / 2;
  const pxPerM = pixelsPerMetreZ(ve, offsetSpan, plotW);
  // A pane with no width or height (first frame, before the ResizeObserver
  // reports) would otherwise produce an infinite or zero-height domain and a
  // pile of NaN path data.
  const metres = pxPerM > 0 && plotH > 0 ? plotH / pxPerM : Math.max(span.hi - span.lo, 1) * 1.2;
  const half = Math.max(metres, 1e-6) / 2;
  return [mid - half, mid + half];
}

/**
 * Pick the preset whose drawn section fills about 60 % of the pane height
 * (spec §4d L640–642). "About" is measured as a ratio, not a difference:
 * exaggeration is a multiplicative quantity, so 5× and 20× are equally far
 * from 10×, and picking by absolute difference would bias every choice
 * upwards towards the coarse end of the list.
 *
 * Stops that would CLIP are not candidates at all. `zDomain` sizes the domain
 * from the exaggeration, so a stop taller than the pane can show does not
 * shrink the drawing to fit — it silently leaves the top and the bottom of the
 * structure off the sheet, which on a compliance drawing is worse than a
 * cramped one: the reader cannot tell there is anything missing. The target
 * fill is 60 % and the ceiling is 100 %, so the wanted exaggeration always
 * fits; the filter only ever bites when rounding UP to the nearest stop would
 * overshoot the pane, and then the next stop down is taken instead.
 *
 * `Auto` therefore cannot clip. Not "rarely": a section taller than even the
 * shallowest stop can show — 12 m of relief across a 4 m swath, which a
 * deviation run against a cutting really does produce — falls through to
 * `ladderStopBelow`, so the ladder runs out but the guarantee does not. The
 * `Auto` button labels whichever factor came back, so nothing is unstated.
 *
 * The ceiling is NOT extended the same way, and that asymmetry is the point.
 * Running past 100× means magnifying a millimetre of survey noise into a
 * landscape and labelling it a road — `pixelsPerMetreZ` is exact, so every
 * bump on that drawing is a claim. Topping out draws such a section small,
 * which is honest. Clipping one draws it wrong.
 */
export function autoExaggeration(zSpan: number, offsetSpan: number, plotW: number, plotH: number): number {
  const pxPerMetreX = offsetSpan > 0 ? plotW / offsetSpan : 0;
  if (!(zSpan > 0) || !(pxPerMetreX > 0) || !(plotH > 0)) return VE_FALLBACK;
  const wanted = (0.6 * plotH) / (zSpan * pxPerMetreX);
  /** The largest exaggeration whose drawn section still fits the pane. */
  const fits = plotH / (zSpan * pxPerMetreX);
  let best: number | null = null;
  let bestErr = Infinity;
  for (const p of VE_AUTO_PRESETS) {
    if (p > fits) continue;
    const err = Math.abs(Math.log(p / wanted));
    if (err < bestErr) {
      bestErr = err;
      best = p;
    }
  }
  return best ?? ladderStopBelow(fits);
}

/**
 * The 1-2-5 stop at or below `v` — the ladder continued past its shallow end
 * for a section too tall for any listed stop.
 *
 * Rounding DOWN, never to nearest, is what makes it a fit rather than a near
 * miss. Staying on 1-2-5 is what keeps the factor a number a reader can hold:
 * `Auto (0.1×)` is an exaggeration, `Auto (0.1667×)` is a diagnostic.
 */
function ladderStopBelow(v: number): number {
  if (!(v > 0) || !Number.isFinite(v)) return VE_FALLBACK;
  const decade = Math.pow(10, Math.floor(Math.log10(v)));
  const lead = v / decade;
  return (lead >= 5 ? 5 : lead >= 2 ? 2 : 1) * decade;
}

/**
 * Whether the drawn domain leaves part of the section off the pane.
 *
 * A hand-chosen exaggeration is allowed to clip — that is the reader asking
 * for a closer look, and `Auto` is one click away. What is not allowed is
 * clipping SILENTLY, so the chart asks this and says so on the drawing. An
 * unmarked section that omits the top of the structure is not a hard-to-read
 * drawing, it is a wrong one.
 */
export function domainClipsSection(span: SectionSpan, domain: readonly [number, number]): boolean {
  if (!span.valid) return false;
  // Rounding slack only. `zDomain` centres the domain on the section, so an
  // exactly-fitting section lands on both edges to within float error and
  // must not be reported as clipped.
  const eps = Math.max(1e-9, (span.hi - span.lo) * 1e-9);
  return span.lo < domain[0] - eps || span.hi > domain[1] + eps;
}

/**
 * `-10 → "10L"`, `5 → "5R"`, `0 → "CL"`. The sign of an offset is the one
 * thing a reader must never have to infer from which end of the axis it sat
 * on, so the side is spelled out on every tick (spec §4d).
 */
export function offsetTickLabel(offset: number): string {
  if (Math.abs(offset) < 1e-6) return 'CL';
  const a = Math.abs(offset);
  // Ticks land on round numbers most of the time; when they do not, showing
  // `2.5L` as `2L` would be a wrong label, not a tidy one.
  const text = Number.isInteger(a) ? String(a) : String(Number(a.toFixed(2)));
  return `${text}${offset < 0 ? 'L' : 'R'}`;
}

/**
 * The centre offset of column `j`. The worker bins with
 * `floor((offset + left) / step)` (`station_bins.py`), so column `j` covers
 * `[start + j·step, start + (j+1)·step)` and its sample sits in the middle.
 */
export function offsetCentre(offsetStartM: number, offsetStepM: number, j: number): number {
  return offsetStartM + (j + 0.5) * offsetStepM;
}

/**
 * The station LINE nearest an internal station.
 *
 * A section is cut at a plane, so the question "which section am I looking at"
 * has a nearest answer, not a containing one — unlike a bin, which a station
 * falls *inside*. Ties go to the lower line, so walking forward through an
 * exact midpoint does not flicker between two sections.
 *
 * `stations` must be ascending, which is how the worker writes them.
 */
export function nearestStationLine(stations: readonly { staInternalM: number }[], station: number): number {
  if (stations.length === 0) return 0;
  let best = 0;
  let bestD = Infinity;
  for (let k = 0; k < stations.length; k++) {
    const d = Math.abs(stations[k].staInternalM - station);
    // Strictly less: the first of two equidistant lines wins.
    if (d < bestD) {
      bestD = d;
      best = k;
    }
  }
  return best;
}

/** The column under an offset, clamped to the swath. */
export function nearestOffsetIndex(
  offset: number,
  offsetStartM: number,
  offsetStepM: number,
  nOffsets: number,
): number {
  if (!(offsetStepM > 0) || nOffsets <= 0) return 0;
  const j = Math.round((offset - offsetStartM) / offsetStepM - 0.5);
  return Math.max(0, Math.min(nOffsets - 1, j));
}

/**
 * The contiguous runs of measured columns in one surface — the pieces a
 * polyline may join. Drawing straight through a gap invents a surface where
 * nothing was measured, which on a volume deliverable is a claim, not a
 * cosmetic shortcut.
 */
export function validRuns(values: Float32Array): OffsetRun[] {
  const runs: OffsetRun[] = [];
  let start = -1;
  for (let i = 0; i < values.length; i++) {
    if (finite(values[i])) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      runs.push({ from: start, to: i - 1 });
      start = -1;
    }
  }
  if (start >= 0) runs.push({ from: start, to: values.length - 1 });
  return runs;
}

/**
 * The runs where NEITHER surface was measured. The worker writes all three
 * planes from the same `n_cell > 0` mask, so both-NaN is exactly "no cell of
 * the analysis grid fell in this offset bin" — a hole, which the section
 * marks, as opposed to `dz = 0`, which is two surfaces that meet.
 */
export function noDataRuns(zTop: Float32Array, zBottom: Float32Array): OffsetRun[] {
  return noDataRunsAcross([zTop, zBottom]);
}

/**
 * The runs where NO level of the stack was measured — the generalisation of
 * `noDataRuns`.
 *
 * A column where the binder was surveyed but the wearing course was not is
 * NOT a hole: something was measured there, and the missing level shows as a
 * break in its own polyline. Only a column no level reached is ground the
 * analysis never covered.
 */
export function noDataRunsAcross(planes: readonly Float32Array[]): OffsetRun[] {
  const n = planes.length === 0 ? 0 : Math.min(...planes.map((p) => p.length));
  const runs: OffsetRun[] = [];
  let start = -1;
  for (let i = 0; i < n; i++) {
    const gap = planes.every((p) => !finite(p[i]));
    if (gap) {
      if (start < 0) start = i;
    } else if (start >= 0) {
      runs.push({ from: start, to: i - 1 });
      start = -1;
    }
  }
  if (start >= 0) runs.push({ from: start, to: n - 1 });
  return runs;
}

/**
 * The cut and fill bands between the two surfaces, as closed polygons in
 * metres.
 *
 * Per-column rectangles would be simpler, but they draw a staircase where
 * the data is a profile, and they cannot show where the two surfaces cross:
 * the crossing would land on a column edge instead of on the point where
 * `dz` actually reaches zero. Here a sign change splits the band at the
 * interpolated crossing, so the boundary between a cut hatch and a fill
 * hatch is the daylight line itself.
 *
 * `zTop < zBottom` is CUT (survey below design), matching the worker's
 * `dz < 0 is CUT` convention (`station_bins.py`).
 */
export function bandPolygons(
  zTop: Float32Array,
  zBottom: Float32Array,
  offsetStartM: number,
  offsetStepM: number,
): BandPolygon[] {
  const n = Math.min(zTop.length, zBottom.length);
  const out: BandPolygon[] = [];

  let kind: 'cut' | 'fill' = 'fill';
  let top: SectionPoint[] = [];
  let bottom: SectionPoint[] = [];
  let peak = 0;

  const flush = () => {
    // Two columns make a band; a lone column has no width, and a run where
    // the surfaces never part has no area. Neither is worth a hatch.
    if (top.length >= 2 && peak > 0) {
      out.push({ kind, points: [...top, ...bottom.slice().reverse()] });
    }
    top = [];
    bottom = [];
    peak = 0;
  };

  let prev: { o: number; t: number; b: number; d: number } | null = null;
  for (let j = 0; j < n; j++) {
    const t = zTop[j];
    const b = zBottom[j];
    if (!finite(t) || !finite(b)) {
      flush();
      prev = null;
      continue;
    }
    const o = offsetCentre(offsetStartM, offsetStepM, j);
    const d = t - b;
    const thisKind: 'cut' | 'fill' = d < 0 ? 'cut' : 'fill';

    if (prev && thisKind !== kind && prev.d !== d) {
      // The surfaces cross between the two samples: close this band on the
      // crossing point and open the next one there, so the two hatches meet
      // exactly where dz = 0.
      const f = prev.d / (prev.d - d);
      const cross: SectionPoint = { o: prev.o + (o - prev.o) * f, z: prev.t + (t - prev.t) * f };
      top.push(cross);
      bottom.push(cross);
      flush();
      top.push({ ...cross });
      bottom.push({ ...cross });
    }
    if (top.length === 0) peak = 0;
    kind = thisKind;
    top.push({ o, z: t });
    bottom.push({ o, z: b });
    peak = Math.max(peak, Math.abs(d));
    prev = { o, t, b, d };
  }
  flush();
  return out;
}

/**
 * The contiguous column runs where the section is out of tolerance against
 * an expectation (spec §8.2, design doc §4.4).
 *
 * `under` and `over` are kept apart rather than merged into one "out of spec"
 * kind: too thin and too thick are different defects with different remedies,
 * and a drawing that renders them alike is one a site team cannot act on.
 *
 * `crossing` is a THIRD kind, never a severe `under`. Where a thickness was
 * expected (`e > 0`), `dz < 0` means the two surfaces pass through each
 * other — the layers are in the wrong order, or a surface is wrong, which is
 * a different conversation from "add 15 mm here" (design doc §4.4:
 * `expectation.py`'s `crossingPct` is its own share, excluded from
 * `meanThicknessM`, and never folded in as a very thin layer). Classified
 * FIRST: a crossing is also far below tolerance, so testing `under` first
 * would swallow every crossing as a thin stretch.
 *
 * The `e > 0` guard matters: in `MATCH` mode the expected difference is
 * zero, so a negative `dz` is simply the other side of a two-sided check,
 * not a crossing.
 *
 * A data hole breaks a run, exactly as it does in `validRuns` — drawing
 * through one would claim a measurement nobody made.
 */
export function toleranceRuns(
  zTop: Float32Array,
  zBottom: Float32Array,
  expected: number | Float32Array,
  underTol: number | null,
  overTol: number | null,
): { from: number; to: number; kind: 'under' | 'over' | 'crossing' }[] {
  const n = Math.min(zTop.length, zBottom.length);
  const out: { from: number; to: number; kind: 'under' | 'over' | 'crossing' }[] = [];
  let start = -1;
  let kind: 'under' | 'over' | 'crossing' | null = null;

  const flush = (end: number) => {
    if (start >= 0 && kind) out.push({ from: start, to: end, kind });
    start = -1;
    kind = null;
  };

  for (let j = 0; j < n; j++) {
    const t = zTop[j];
    const b = zBottom[j];
    const e = typeof expected === 'number' ? expected : expected[j];
    let k: 'under' | 'over' | 'crossing' | null = null;
    if (finite(t) && finite(b) && finite(e)) {
      const dz = t - b;
      const dev = dz - e;
      // Order matters: a crossing is ALSO far below tolerance, so testing
      // `under` first would swallow every crossing as a thin stretch.
      if (e > 0 && dz < 0) k = 'crossing';
      else if (underTol != null && dev < -underTol) k = 'under';
      else if (overTol != null && dev > overTol) k = 'over';
    }
    if (k !== kind) {
      flush(j - 1);
      if (k) {
        start = j;
        kind = k;
      }
    }
  }
  flush(n - 1);
  return out;
}

// ---------------------------------------------------------------------------
// The multi-layer stack (multi-layer thickness §6.2).
//
// A construction stack is N LEVELS (top of wearing course, top of binder, …
// formation) giving N−1 LAYERS of material between them. `station_sections.f32`
// carries 2N planes for a stack: `asbuilt:<level>` for every level in level
// order, then `design:<level>` for every level
// (`scripts/stack_analysis.py::_plane_names`).
// ---------------------------------------------------------------------------

/** What a stack's as-built planes are named. */
export const AS_BUILT_PLANE_PREFIX = 'asbuilt:';
/** What a stack's design planes are named. */
export const DESIGN_PLANE_PREFIX = 'design:';

/**
 * As-built plane indices, newest format first, legacy second.
 *
 * A stack names planes `asbuilt:*` then `design:*`; every run made before the
 * stack named exactly two, `zTop` and `zBottom`, with **no prefix**. Those
 * files are still on S3 and still opened daily, so a bare prefix filter would
 * find zero as-built planes and render an EMPTY cross-section for every
 * existing analysis, while every new stack run looked perfect. The fallback is
 * not a tidiness wart; it is the only thing standing between this refactor and
 * blanking every historical section.
 */
export function asBuiltPlaneIndices(planeNames: readonly string[]): number[] {
  const prefixed = planeNames.map((n, i) => (n.startsWith(AS_BUILT_PLANE_PREFIX) ? i : -1)).filter((i) => i >= 0);
  if (prefixed.length > 0) return prefixed;
  // Legacy: the two unprefixed planes ARE the pair, top then bottom.
  return planeNames.map((_, i) => i);
}

/**
 * Design plane indices — and, unlike `asBuiltPlaneIndices`, **no fallback**.
 *
 * The asymmetry is deliberate. A legacy file's two planes are a real as-built
 * pair, so treating them as one is right. It carries no design raster at all
 * (surface-expectations §8.2a), so falling back to "all of them" here would
 * draw the survey a second time as its own design reference line — a drawing
 * that claims the survey matches the design exactly, everywhere.
 */
export function designPlaneIndices(planeNames: readonly string[]): number[] {
  return planeNames.map((n, i) => (n.startsWith(DESIGN_PLANE_PREFIX) ? i : -1)).filter((i) => i >= 0);
}

/**
 * The LEVEL a plane belongs to: `asbuilt:Top of binder` → `Top of binder`.
 * A legacy unprefixed name is already the only name it has.
 */
export function levelName(planeName: string): string {
  if (planeName.startsWith(AS_BUILT_PLANE_PREFIX)) return planeName.slice(AS_BUILT_PLANE_PREFIX.length);
  if (planeName.startsWith(DESIGN_PLANE_PREFIX)) return planeName.slice(DESIGN_PLANE_PREFIX.length);
  return planeName;
}

/** One station line's worth of columns out of a whole `.f32` plane. */
export function planeRowAt(plane: Float32Array, k: number, nOffsets: number): Float32Array {
  const a = k * nOffsets;
  return plane.subarray(a, a + nOffsets);
}

/** How the run's expectation reaches each layer. */
export interface SectionLayerExpectation {
  /** The scalar expected thickness — `0` for MATCH, a design value for
   *  CONSTANT, `null` when it has to come from the design planes. */
  expectedM: number | null;
  /**
   * SURFACES mode: the expected thickness is not a scalar at all, it is the
   * gap between this layer's two DESIGN planes, per offset column. That is
   * exactly the per-column raster surface-expectations §8.2a said never
   * reached the client — a stack file carries it.
   */
  fromDesignPlanes: boolean;
  underToleranceM: number | null;
  overToleranceM: number | null;
}

/**
 * How `sectionLayers` is told what to judge each layer against.
 *
 * ONE object applies to every layer — the single-pair run, whose expectation
 * is a property of the RUN. An ARRAY is one entry per layer, which is what a
 * stack needs: its layers have different design thicknesses AND different
 * tolerances (`stack_analysis.py` judges layer `i` against level `i`'s), so a
 * single run-wide pair of numbers would mark the wrong stretches. A `null`
 * entry means that layer is not judged at all.
 */
export type SectionLayerExpectations = SectionLayerExpectation | readonly (SectionLayerExpectation | null)[] | null;

/** One LAYER of the stack: the material between two adjacent levels. */
export interface SectionLayerGeometry {
  /** Layer index. Layer `i` lies between levels `i` and `i + 1`. */
  i: number;
  topLevel: number;
  bottomLevel: number;
  /**
   * False when no offset column had BOTH bounding levels — the layer is not
   * built yet (or was not surveyed). `PENDING` is a state, never a
   * zero-thickness layer, so the section draws nothing at all for it rather
   * than a band that reads as "measured, and it came out at 0 mm".
   */
  measured: boolean;
  /** The filled band, split at every crossing exactly as the pair version. */
  bands: BandPolygon[];
  /** This layer's OWN out-of-tolerance runs. */
  out: { from: number; to: number; kind: 'under' | 'over' | 'crossing' }[];
  /** The layer's own elevation extent, for a band-local overlay. */
  span: SectionSpan;
}

/**
 * The N−1 layers between N as-built levels, each judged against its own
 * design thickness.
 *
 * "Its own" is the whole point. A stack's layers have different design
 * thicknesses — 40 mm of wearing course over 60 mm of binder — so one shared
 * expected value would mark the wrong layer. Where the file carries design
 * planes, layer `i`'s expected thickness is `design[i] − design[i+1]` per
 * COLUMN, which also handles a crossfall the two surfaces share.
 *
 * The TOLERANCES are per-layer too, for the same reason and by the same route:
 * pass one `SectionLayerExpectation` per layer (see `SectionLayerExpectations`)
 * and layer `i` is judged against entry `i`. A stack's caller builds that list
 * from the LEVELS, taking level `i`'s pair for layer `i` — the top of the two,
 * which is the pair `stack_analysis.py` judged it with.
 */
export function sectionLayers(
  levels: readonly Float32Array[],
  designs: readonly Float32Array[],
  offsetStartM: number,
  offsetStepM: number,
  expectation: SectionLayerExpectations,
): SectionLayerGeometry[] {
  const out: SectionLayerGeometry[] = [];
  for (let i = 0; i + 1 < levels.length; i++) {
    const top = levels[i];
    const bottom = levels[i + 1];
    const n = Math.min(top.length, bottom.length);
    let measured = false;
    for (let j = 0; j < n; j++) {
      if (finite(top[j]) && finite(bottom[j])) {
        measured = true;
        break;
      }
    }
    // No `measured ?` guard here, and none on `out` below. Both used to be
    // written, and both were provably dead: `measured` is exactly "some
    // column has BOTH levels finite", which is the same condition
    // `bandPolygons` and `toleranceRuns` each apply per column — so an
    // unmeasured layer yields `[]` from either of them on its own, and no
    // fixture can tell the guarded version from the unguarded one. A guard
    // nothing can redden reads as load-bearing and is not; `measured` is the
    // flag the CALLER gates the whole layer group on (`StationSectionChart`),
    // which is where the absence is enforced and where a mutation does redden.
    const bands = bandPolygons(top, bottom, offsetStartM, offsetStepM);

    // Layer `i` is judged against LEVEL `i`'s tolerances — the TOP of its
    // pair, which is what the worker does
    // (`stack_analysis.py`: `_judge(actual, design, levels[a].underTolM,
    // levels[a].overTolM, 'SURFACES')`). The caller resolves that mapping and
    // hands one entry per layer; a single object applies to every layer, which
    // is the single-pair run and every run whose expectation is run-wide.
    const layerExpectation = Array.isArray(expectation)
      ? ((expectation[i] ?? null) as SectionLayerExpectation | null)
      : (expectation as SectionLayerExpectation | null);

    let expected: number | Float32Array | null = null;
    if (layerExpectation) {
      if (layerExpectation.fromDesignPlanes) {
        const dTop = designs[i];
        const dBottom = designs[i + 1];
        if (dTop && dBottom) {
          const m = Math.min(dTop.length, dBottom.length, n);
          const e = new Float32Array(m);
          for (let j = 0; j < m; j++) e[j] = dTop[j] - dBottom[j];
          expected = e;
        }
        // No design planes (a run made before the stack): nothing to compare
        // each column against, so nothing is claimed. Same outcome as a run
        // with no expectation at all.
      } else {
        expected = layerExpectation.expectedM;
      }
    }

    out.push({
      i,
      topLevel: i,
      bottomLevel: i + 1,
      measured,
      bands,
      out:
        layerExpectation && expected != null
          ? toleranceRuns(top, bottom, expected, layerExpectation.underToleranceM, layerExpectation.overToleranceM)
          : [],
      span: sectionSpansAcross([top, bottom]),
    });
  }
  return out;
}
